<template>
  <div class="space-y-4">
    <TimelineTransfer
      :allocation="allocation"
      :booking-id="booking.id"
      :type="AllocationType.TRANSFER_DELIVERY"
      @delete="$emit('deleteTransfer', $event)"
    />
    <div class="rounded-lg bg-white p-3 lg:p-4">
      <div class="space-y-2">
        <div class="flex items-start gap-2 lg:items-center lg:gap-4">
          <img :src="carProp(allocation.car, 'imageUrl') ?? ''" alt="Car" class="h-8" />
          <div
            class="flex min-w-0 flex-1 flex-col justify-between gap-2 lg:flex-row lg:items-center lg:gap-4"
            data-testid="timeline-allocation"
          >
            <div class="flex shrink-0 grow flex-col items-start">
              <div class="flex flex-col lg:flex-row lg:items-center lg:gap-2">
                <span class="font-medium"
                  >{{ allocation.car.vehicleType?.make.name }}
                  {{ allocation.car.vehicleType?.model }}</span
                >
                <LicencePlate class="mb-0 self-start">{{
                  allocation.car.licencePlate
                }}</LicencePlate>
              </div>
              <p class="flex items-center gap-1 font-mono text-xs tracking-wider text-obsidian-500">
                <template v-if="!allocation.car?.isGhostCar">
                  <ScanBarcodeIcon class="size-4 text-obsidian-900 opacity-30" />
                  <span class="inline-block">{{ allocation.car.fin || '-' }}</span>
                </template>
                <HashIcon class="size-4 text-obsidian-900 opacity-30" />
                <span class="inline-block">{{ allocation.car.fleetNumber }}</span>
              </p>
              <div class="text-xs text-obsidian-500 lg:text-sm">
                {{ t('from') }}
                {{
                  getLocalizedField(
                    getStationOfCarByDate(allocation.car, allocation.startDate).info,
                    locale,
                  )
                }}
              </div>
              <div
                v-if="allocation.carSwapReason || allocation.comment"
                class="w-0 min-w-full cursor-pointer truncate text-xs text-obsidian-500 lg:text-sm"
                @click="showSwapDetailsModal = true"
              >
                <span class="font-medium">{{ t('reason') }}:</span>
                {{ getCarSwapPreview() }}
              </div>
            </div>
            <Badge
              v-if="
                [
                  CarSwapReason.FLAT_TYRE,
                  CarSwapReason.TECHNICAL_DEFECT,
                  CarSwapReason.ACCIDENT,
                ].includes(allocation.carSwapReason!)
              "
              variant="problem"
              size="md"
              class="shrink-0"
            >
              {{ t('replacementCar') }}
            </Badge>
            <div v-if="deliveryTransfer || returnTransfer" class="flex min-w-0 flex-col gap-1">
              <div
                v-if="deliveryTransfer"
                class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1"
              >
                <span class="max-w-full shrink-0 truncate text-sm text-obsidian-500"
                  >{{ t('handingOverHub') }}:</span
                >
                <AllocationStationSelect
                  :allocation="allocation"
                  :booking-id="booking.id"
                  mode="pickup"
                  :placeholder="t('handingOverHub')"
                  :home-station-id="getStationOfCarByDate(allocation.car, allocation.startDate).id"
                />
              </div>
              <div
                v-if="returnTransfer"
                class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1"
              >
                <span class="max-w-full shrink-0 truncate text-sm text-obsidian-500"
                  >{{ t('returningHub') }}:</span
                >
                <AllocationStationSelect
                  :allocation="allocation"
                  :booking-id="booking.id"
                  mode="dropoff"
                  :placeholder="t('returningHub')"
                  :home-station-id="getStationOfCarByDate(allocation.car, allocation.endDate).id"
                />
              </div>
            </div>
            <div
              v-if="isFirstAllocation && !isLongTerm"
              class="flex shrink-0 items-start gap-6 pr-2 lg:pr-0"
            >
              <CustomTotalBasePrice
                :booking="booking"
                :disabled="
                  !can('create', pricingSubject(booking.priceCalculation), 'customTotalBasePrice')
                "
              />
            </div>
            <div class="flex shrink-0 justify-end">
              <div
                v-if="
                  allocation.status !== AllocationStatus.HANDED_OVER &&
                  allocation.status !== AllocationStatus.RETURNED
                "
                class="whitespace-nowrap"
              >
                <CVButton
                  v-if="!isFirstAllocation"
                  variant="ghost"
                  :title="t('deleteCarSwap')"
                  size="icon"
                  @click.prevent="handleDelete"
                  ><TrashIcon class="size-4 text-red-500"
                /></CVButton>
                <CVButton
                  v-if="!isFirstAllocation"
                  variant="ghost"
                  :title="t('editCarSwap')"
                  size="icon"
                  @click.prevent="emit('edit')"
                  ><PencilIcon class="size-4"
                /></CVButton>
              </div>
              <div v-else>
                <AllocationStatusBadge :status="allocation.status" size="md" />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="hasVaryingBookedCar"
          class="flex items-center gap-2 rounded-sm bg-obsidian-50 px-4 py-1"
        >
          <span>{{ t('upgradedFrom') }}:</span>
          <LicencePlate class="mb-0 self-start">{{ booking.bookedCar.licencePlate }}</LicencePlate>
          <span
            >{{ booking.bookedCar.vehicleType.make.name }}
            {{ booking.bookedCar.vehicleType.model }}</span
          >
        </div>
      </div>
    </div>
    <TimelineTransfer
      :allocation="allocation"
      :booking-id="booking.id"
      :type="AllocationType.TRANSFER_RETURN"
      @delete="$emit('deleteTransfer', $event)"
    />
    <Modal v-model="showSwapDetailsModal">
      <div class="space-y-3">
        <div v-if="allocation.carSwapReason">
          <div class="text-xs text-obsidian-500">{{ t('carSwapReason') }}</div>
          <div class="font-medium">
            {{ t(`carSwapReasons.${allocation.carSwapReason}`) }}
          </div>
        </div>
        <div v-if="allocation.comment">
          <div class="text-xs text-obsidian-500">{{ t('comment') }}</div>
          <div class="whitespace-pre-wrap">{{ allocation.comment }}</div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { carProp } from '@/utils/car-prop';
import type { Allocation } from '@/entities/allocations/allocation.entity';
import LicencePlate from '../LicencePlate.vue';
import { HashIcon, PencilIcon, ScanBarcodeIcon, TrashIcon } from 'lucide-vue-next';
import { AllocationStatus, AllocationType, CarSwapReason } from '@carvia/ros-client-types';
import { Alert } from '@/services/alert.service';
import { useI18n } from 'vue-i18n';
import Badge from '@/components/Badge.vue';
import AllocationStatusBadge from '@/components/badges/AllocationStatusBadge.vue';
import { getStationOfCarByDate } from '@/hooks/use-current-station-of-car';
import { getLocalizedField } from '@/utils/get-localized-field';
import TimelineTransfer from './TimelineTransfer.vue';
import AllocationStationSelect from './AllocationStationSelect.vue';
import { computed, ref } from 'vue';
import CVButton from '../buttons/CVButton.vue';
import Modal from '../Modal.vue';
import type { Booking } from '@/entities/bookings/booking.entity.ts';
import CustomTotalBasePrice from '@/components/booking-forms/CustomTotalBasePrice.vue';
import { useAbility } from '@casl/vue';
import { isLongTermPricing, pricingSubject } from '@/utils/pricing.utils';

const { t, locale } = useI18n();
const { can } = useAbility();

const props = defineProps<{
  allocation: Allocation;
  booking: Booking;
}>();

const emit = defineEmits<{
  (e: 'edit'): void;
  (e: 'delete'): void;
  (e: 'deleteTransfer', transfer: Allocation): void;
}>();

const showSwapDetailsModal = ref(false);
const isFirstAllocation = computed(
  () => props.booking.allocations.findIndex((a) => a.id === props.allocation.id) === 0,
);
const isLongTerm = computed(() => isLongTermPricing(props.booking.priceCalculation));

const hasVaryingBookedCar = computed(
  () => isFirstAllocation.value && props.allocation.car.id !== props.booking.bookedCar.id,
);

const deliveryTransfer = computed(() =>
  props.allocation.transfers?.find((tr) => tr.type === AllocationType.TRANSFER_DELIVERY),
);

const returnTransfer = computed(() =>
  props.allocation.transfers?.find((tr) => tr.type === AllocationType.TRANSFER_RETURN),
);

const getCarSwapPreview = () => {
  const parts: string[] = [];
  if (props.allocation.carSwapReason) {
    parts.push(t(`carSwapReasons.${props.allocation.carSwapReason}`));
  }
  if (props.allocation.comment) {
    parts.push(props.allocation.comment);
  }
  return parts.join(' — ');
};

const handleDelete = async () => {
  const result = await Alert.fire({
    title: t('deleteCarSwap'),
    text: t('deleteCarSwapText'),
    icon: 'warning',
    showConfirmButton: false,
    showDenyButton: true,
    showCancelButton: true,
    denyButtonText: t('delete'),
    cancelButtonText: t('cancel'),
  });
  if (result.isDenied) {
    emit('delete');
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "deleteCarSwap": "Delete car swap",
    "deleteCarSwapText": "Are you sure you want to delete this car swap?",
    "delete": "Delete",
    "cancel": "Cancel",
    "from": "from",
    "editCarSwap": "Edit car swap",
    "carSwapReason": "Reason",
    "comment": "Comment",
    "reason": "Reason",
    "upgradedFrom": "Upgraded from",
    "replacementCar": "Replacement car",
    "handingOverHub": "Handing-over hub",
    "returningHub": "Returning hub"
  },
  "de": {
    "deleteCarSwap": "Autotausch löschen",
    "deleteCarSwapText": "Sind Sie sicher, dass Sie diesen Autotausch löschen möchten?",
    "delete": "Löschen",
    "cancel": "Abbrechen",
    "from": "von",
    "editCarSwap": "Autotausch bearbeiten",
    "carSwapReason": "Grund",
    "comment": "Kommentar",
    "reason": "Grund",
    "upgradedFrom": "Upgegradet von",
    "replacementCar": "Ersatzwagen",
    "handingOverHub": "Übergebender Hub",
    "returningHub": "Rücknehmender Hub"
  },
  "es": {
    "deleteCarSwap": "Eliminar cambio de vehículo",
    "deleteCarSwapText": "¿Seguro que desea eliminar este cambio de vehículo?",
    "delete": "Eliminar",
    "cancel": "Cancelar",
    "from": "desde",
    "editCarSwap": "Editar cambio de vehículo",
    "carSwapReason": "Motivo",
    "comment": "Comentario",
    "reason": "Motivo",
    "upgradedFrom": "Actualizado desde",
    "replacementCar": "Vehículo de sustitución",
    "handingOverHub": "Hub de entrega",
    "returningHub": "Hub de devolución"
  }
}
</i18n>
